/* ============================================================
   PupStyle Homepage JS  ·  ps-homepage.js
   Vanilla JS, kein globaler Namespace, strict mode.
   Einbindung: <script src="{{ 'ps-homepage.js' | asset_url }}" defer></script>
   Idempotent: kann mehrfach geladen werden ohne Doppel-Init.
   ============================================================ */
(function () {
  "use strict";

  var REDUCED = window.matchMedia("(prefers-reduced-motion: reduce)").matches;

  /* ----------------------------------------------------------
     1. Scroll-Reveal — IntersectionObserver für [data-ps-animate]
     ---------------------------------------------------------- */
  function initReveal() {
    var nodes = document.querySelectorAll("[data-ps-animate]:not([data-ps-revealed])");
    if (!nodes.length) return;

    if (REDUCED || !("IntersectionObserver" in window)) {
      nodes.forEach(function (n) {
        n.classList.add("ps-is-visible");
        n.setAttribute("data-ps-revealed", "");
      });
      return;
    }

    var io = new IntersectionObserver(function (entries, obs) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) return;
        entry.target.classList.add("ps-is-visible");
        obs.unobserve(entry.target);
      });
    }, { rootMargin: "0px 0px -10% 0px", threshold: 0.12 });

    nodes.forEach(function (n) {
      n.setAttribute("data-ps-revealed", "");
      io.observe(n);
    });
  }

  /* ----------------------------------------------------------
     2. Akkordeon / FAQ — Custom-Toggle mit ARIA
     (für Sektionen, die NICHT <details> nutzen)
     Markup:  [data-ps-accordion] > [data-ps-acc-item]
              button[data-ps-acc-trigger] + [data-ps-acc-panel]
     ---------------------------------------------------------- */
  function initAccordions() {
    document.querySelectorAll("[data-ps-accordion]:not([data-ps-acc-ready])").forEach(function (acc) {
      acc.setAttribute("data-ps-acc-ready", "");
      var single = acc.getAttribute("data-ps-acc-single") === "true";
      var triggers = acc.querySelectorAll("[data-ps-acc-trigger]");

      triggers.forEach(function (trigger) {
        trigger.addEventListener("click", function () {
          var expanded = trigger.getAttribute("aria-expanded") === "true";
          if (single && !expanded) {
            triggers.forEach(function (t) {
              if (t !== trigger) setAcc(t, false);
            });
          }
          setAcc(trigger, !expanded);
        });
      });
    });

    function setAcc(trigger, open) {
      var panel = document.getElementById(trigger.getAttribute("aria-controls"));
      trigger.setAttribute("aria-expanded", String(open));
      if (!panel) return;
      panel.hidden = !open;
    }
  }

  /* ----------------------------------------------------------
     3. Tab-Interface (Bestseller-Section) — ARIA-konform
     Markup:  [data-ps-tabs]
              [role=tab][aria-controls] + [role=tabpanel][id]
     ---------------------------------------------------------- */
  function initTabs() {
    document.querySelectorAll("[data-ps-tabs]:not([data-ps-tabs-ready])").forEach(function (wrap) {
      wrap.setAttribute("data-ps-tabs-ready", "");
      var tabs = Array.prototype.slice.call(wrap.querySelectorAll('[role="tab"]'));
      if (!tabs.length) return;

      function activate(tab, focus) {
        tabs.forEach(function (t) {
          var selected = t === tab;
          t.setAttribute("aria-selected", String(selected));
          t.tabIndex = selected ? 0 : -1;
          var panel = document.getElementById(t.getAttribute("aria-controls"));
          if (panel) panel.hidden = !selected;
        });
        if (focus) tab.focus();
      }

      tabs.forEach(function (tab, i) {
        tab.addEventListener("click", function () { activate(tab, false); });
        tab.addEventListener("keydown", function (e) {
          var idx = null;
          if (e.key === "ArrowRight" || e.key === "ArrowDown") idx = (i + 1) % tabs.length;
          else if (e.key === "ArrowLeft" || e.key === "ArrowUp") idx = (i - 1 + tabs.length) % tabs.length;
          else if (e.key === "Home") idx = 0;
          else if (e.key === "End") idx = tabs.length - 1;
          if (idx !== null) { e.preventDefault(); activate(tabs[idx], true); }
        });
      });
    });
  }

  /* ----------------------------------------------------------
     4. Karussell — native Scroll-Snap + Pfeil-Navigation
     Markup:  [data-ps-carousel]
              [data-ps-track] + [data-ps-prev] + [data-ps-next]
     ---------------------------------------------------------- */
  function initCarousels() {
    document.querySelectorAll("[data-ps-carousel]:not([data-ps-car-ready])").forEach(function (car) {
      car.setAttribute("data-ps-car-ready", "");
      var track = car.querySelector("[data-ps-track]");
      var prev = car.querySelector("[data-ps-prev]");
      var next = car.querySelector("[data-ps-next]");
      if (!track) return;

      function step() {
        var first = track.firstElementChild;
        if (!first) return track.clientWidth * 0.8;
        var gap = parseFloat(getComputedStyle(track).columnGap || getComputedStyle(track).gap || 0) || 0;
        return first.getBoundingClientRect().width + gap;
      }
      function update() {
        if (!prev || !next) return;
        var maxScroll = track.scrollWidth - track.clientWidth - 2;
        prev.disabled = track.scrollLeft <= 2;
        next.disabled = track.scrollLeft >= maxScroll;
      }
      if (prev) prev.addEventListener("click", function () {
        track.scrollBy({ left: -step() * 2, behavior: REDUCED ? "auto" : "smooth" });
      });
      if (next) next.addEventListener("click", function () {
        track.scrollBy({ left: step() * 2, behavior: REDUCED ? "auto" : "smooth" });
      });
      track.addEventListener("scroll", function () {
        window.requestAnimationFrame(update);
      }, { passive: true });
      window.addEventListener("resize", update, { passive: true });
      update();

      /* optionales Autoplay */
      var interval = parseInt(car.getAttribute("data-ps-autoplay"), 10);
      if (interval > 0 && !REDUCED) {
        var timer = null;
        var play = function () {
          timer = window.setInterval(function () {
            var maxScroll = track.scrollWidth - track.clientWidth - 2;
            if (track.scrollLeft >= maxScroll) track.scrollTo({ left: 0, behavior: "smooth" });
            else track.scrollBy({ left: step(), behavior: "smooth" });
          }, interval);
        };
        var stop = function () { window.clearInterval(timer); };
        car.addEventListener("mouseenter", stop);
        car.addEventListener("mouseleave", play);
        car.addEventListener("focusin", stop);
        play();
      }
    });
  }

  /* ----------------------------------------------------------
     5. Countdown-Timer (CTA-Banner)
     Markup:  [data-ps-countdown="2026-12-31T23:59:59"]
              child spans: [data-ps-cd="days|hours|minutes|seconds"]
     ---------------------------------------------------------- */
  function initCountdowns() {
    document.querySelectorAll("[data-ps-countdown]:not([data-ps-cd-ready])").forEach(function (el) {
      el.setAttribute("data-ps-cd-ready", "");
      var target = new Date(el.getAttribute("data-ps-countdown")).getTime();
      if (isNaN(target)) { el.hidden = true; return; }

      var fields = {
        days:    el.querySelector('[data-ps-cd="days"]'),
        hours:   el.querySelector('[data-ps-cd="hours"]'),
        minutes: el.querySelector('[data-ps-cd="minutes"]'),
        seconds: el.querySelector('[data-ps-cd="seconds"]')
      };
      var pad = function (n) { return (n < 10 ? "0" : "") + n; };

      function tick() {
        var diff = target - Date.now();
        if (diff <= 0) {
          el.setAttribute("data-ps-cd-expired", "");
          if (fields.days) fields.days.textContent = "0";
          ["hours", "minutes", "seconds"].forEach(function (k) {
            if (fields[k]) fields[k].textContent = "00";
          });
          window.clearInterval(t);
          return;
        }
        var s = Math.floor(diff / 1000);
        if (fields.days)    fields.days.textContent = Math.floor(s / 86400);
        if (fields.hours)   fields.hours.textContent = pad(Math.floor(s / 3600) % 24);
        if (fields.minutes) fields.minutes.textContent = pad(Math.floor(s / 60) % 60);
        if (fields.seconds) fields.seconds.textContent = pad(s % 60);
      }
      tick();
      var t = window.setInterval(tick, 1000);
    });
  }

  /* ----------------------------------------------------------
     6. Hood-Studio Bild-Karussell (Auto-Fade)
     Markup:  [data-ps-fader] > [data-ps-fade-slide]
     ---------------------------------------------------------- */
  function initFaders() {
    document.querySelectorAll("[data-ps-fader]:not([data-ps-fader-ready])").forEach(function (fader) {
      fader.setAttribute("data-ps-fader-ready", "");
      var slides = fader.querySelectorAll("[data-ps-fade-slide]");
      if (slides.length < 2 || REDUCED) return;
      var ms = parseInt(fader.getAttribute("data-ps-fader"), 10) || 4000;
      var i = 0;

      function show(n) {
        slides.forEach(function (s, idx) {
          s.classList.toggle("ps-fade-active", idx === n);
        });
      }
      if (!("IntersectionObserver" in window)) { autoplay(); return; }

      var io = new IntersectionObserver(function (entries) {
        entries.forEach(function (e) {
          if (e.isIntersecting) { autoplay(); io.disconnect(); }
        });
      }, { threshold: 0.25 });
      io.observe(fader);

      var timer;
      function autoplay() {
        timer = window.setInterval(function () {
          i = (i + 1) % slides.length;
          show(i);
        }, ms);
        fader.addEventListener("mouseenter", function () { window.clearInterval(timer); });
        fader.addEventListener("mouseleave", function () {
          window.clearInterval(timer);
          timer = window.setInterval(function () {
            i = (i + 1) % slides.length; show(i);
          }, ms);
        });
      }
    });
  }

  /* ----------------------------------------------------------
     7. Hero-Parallax — leichter Transform-Effekt via rAF
     Markup:  [data-ps-parallax]
     ---------------------------------------------------------- */
  function initParallax() {
    if (REDUCED) return;
    var nodes = document.querySelectorAll("[data-ps-parallax]:not([data-ps-px-ready])");
    if (!nodes.length) return;
    var ticking = false;

    function frame() {
      nodes.forEach(function (n) {
        var rect = n.getBoundingClientRect();
        if (rect.bottom < 0 || rect.top > window.innerHeight) return;
        var layer = n.querySelector("[data-ps-parallax-layer]");
        if (layer) {
          var offset = (rect.top * -0.15).toFixed(1);
          layer.style.transform = "translate3d(0," + offset + "px,0) scale(1.12)";
        }
      });
      ticking = false;
    }
    nodes.forEach(function (n) { n.setAttribute("data-ps-px-ready", ""); });
    window.addEventListener("scroll", function () {
      if (!ticking) { window.requestAnimationFrame(frame); ticking = true; }
    }, { passive: true });
    frame();
  }

  /* ----------------------------------------------------------
     Init — DOM ready + Shopify Theme-Editor Re-Init
     ---------------------------------------------------------- */
  function initAll() {
    initReveal();
    initAccordions();
    initTabs();
    initCarousels();
    initCountdowns();
    initFaders();
    initParallax();
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", initAll);
  } else {
    initAll();
  }

  /* Shopify Theme-Editor: Sections werden dynamisch nachgeladen */
  if (window.Shopify && Shopify.designMode) {
    document.addEventListener("shopify:section:load", initAll);
    document.addEventListener("shopify:section:select", initReveal);
  }
})();
